import moment from "moment";
import { getStatusPillStyle } from "@/js/activity-status";

/** Units/sections a ticket can come from. `value` is what gets stored. */
export const UNITS = [
  { value: "Accounting", label: "Accounting" },
  { value: "Admin", label: "Admin" },
  { value: "Cashier", label: "Cashier" },
  { value: "CMSP", label: "CMSP" },
  { value: "COA", label: "COA" },
  { value: "General Services", label: "General Services" },
  { value: "Human Resources", label: "Human Resources" },
  { value: "RD", label: "Office of the Regional Director" },
  { value: "Records", label: "Records" },
  { value: "Technical", label: "Technical" },
  { value: "UniFAST", label: "UniFAST" },
];

/**
 * Service types, grouped by who usually handles them. `prompt` is the
 * placeholder for the description: what the support team needs to know.
 */
export const SERVICE_TYPE_GROUPS = [
  {
    label: "Computers and systems",
    tone: "ict",
    types: [
      {
        value: "Troubleshooting",
        icon: "bi-wrench",
        hint: "A computer or device isn't working right.",
        prompt: "Which computer or device, and what's going wrong? When did it start? Include any error message you saw.",
      },
      {
        value: "Printer / Scanner",
        icon: "bi-printer",
        hint: "Printing, scanning, or driver problems.",
        prompt: "Which printer or scanner, and what happens when you use it? Include any error message or blinking light.",
      },
      {
        value: "Network / Internet",
        icon: "bi-wifi",
        hint: "No connection, slow internet, or Wi-Fi trouble.",
        prompt: "Where are you, and is it only your computer or everyone nearby? Wired or Wi-Fi?",
      },
      {
        value: "Hardware Installation",
        icon: "bi-pc-display",
        hint: "Set up a computer, monitor, or other device.",
        prompt: "What needs to be set up, and where? Is the equipment already in the office?",
      },
      {
        value: "Software Installation",
        icon: "bi-download",
        hint: "Install or update a program.",
        prompt: "Which program (and version, if you know), and on which computer?",
      },
      {
        value: "Account / Access Request",
        icon: "bi-key",
        hint: "Email, CCMIS, or other system access.",
        prompt: "Which system or account, and what access is needed? For a new employee, include their full name.",
      },
      {
        value: "CCMIS System Glitch/Bug",
        icon: "bi-bug",
        hint: "Something in CCMIS isn't working as expected.",
        prompt: "Which page were you on, what did you click, and what happened? Include any error message you saw.",
      },
      {
        value: "Data Request",
        icon: "bi-file-earmark-bar-graph",
        hint: "Reports, extracts, or records from a system.",
        prompt: "What data do you need, for what period, and in what format? When do you need it?",
      },
    ],
  },
  {
    label: "Facilities (General Services)",
    tone: "facility",
    types: [
      {
        value: "Air Conditioning System",
        icon: "bi-snow",
        hint: "Not cooling, leaking, or noisy units.",
        prompt: "Which room or unit, and what's wrong: not cooling, leaking, or noisy?",
      },
      {
        value: "Electrical",
        icon: "bi-lightning-charge",
        hint: "Outlets, lights, or wiring.",
        prompt: "Where is it (which outlet, light, or switch), and what's happening?",
      },
      {
        value: "Plumbing",
        icon: "bi-droplet",
        hint: "Leaks, clogged drains, or water supply.",
        prompt: "Where is it, and what's wrong: a leak, a clog, or no water?",
      },
      {
        value: "Building Structure",
        icon: "bi-building",
        hint: "Doors, windows, ceilings, or walls.",
        prompt: "Where is it (door, window, ceiling, or wall), and what's damaged?",
      },
      {
        value: "Repairs",
        icon: "bi-hammer",
        hint: "Furniture or equipment that needs fixing.",
        prompt: "What needs fixing, and where is it? Describe the damage.",
      },
      {
        value: "Maintenance",
        icon: "bi-tools",
        hint: "Regular upkeep of equipment or spaces.",
        prompt: "What needs upkeep, and where? Is it urgent?",
      },
    ],
  },
  {
    label: "Other",
    tone: "other",
    types: [
      {
        value: "Others",
        icon: "bi-three-dots",
        hint: "Anything not listed above.",
        prompt: "Tell us what you need help with.",
      },
    ],
  },
];

const UNKNOWN_TYPE = { icon: "bi-ticket-detailed", hint: "", prompt: "", group: "", tone: "other" };

/** A service type's details, also for older tickets whose type isn't listed. */
export function serviceType(value) {
  for (const group of SERVICE_TYPE_GROUPS) {
    const found = group.types.find((type) => type.value === value);
    if (found) return { ...found, group: group.label, tone: group.tone };
  }
  return { ...UNKNOWN_TYPE, value: value || "" };
}

export function serviceTypeHint(value) {
  return serviceType(value).hint;
}

// color/textColor follow the soft pill scheme used by the calendar.
export const TICKET_STATUSES = {
  Pending: {
    label: "Pending",
    color: "#e0901f",
    textColor: "#986215",
    icon: "bi-hourglass-split",
    hint: "Waiting for the support team to pick it up.",
  },
  "In progress": {
    label: "In progress",
    color: "#4d8dff",
    textColor: "#3a6abf",
    icon: "bi-tools",
    hint: "The support team is working on it.",
  },
  "On hold": {
    label: "On hold",
    color: "#64748b",
    textColor: "#475569",
    icon: "bi-pause-circle",
    hint: "Waiting on the requester, e.g. for the unit to be brought to the MIS office.",
  },
  "Forwarded to GSS": {
    label: "Forwarded to GSS",
    color: "#7c5cff",
    textColor: "#5a3fd1",
    icon: "bi-box-arrow-up-right",
    hint: "Referred to General Services as a job order.",
  },
  Closed: {
    label: "Closed",
    color: "#2bb6a3",
    textColor: "#1d7a6d",
    icon: "bi-check-circle-fill",
    hint: "Resolved.",
  },
};

const UNKNOWN_STATUS = {
  color: "#768192",
  textColor: "#4f5b6b",
  icon: "bi-circle",
  hint: "",
};

export function ticketStatusMeta(status) {
  return TICKET_STATUSES[status] || { ...UNKNOWN_STATUS, label: status || "Unknown" };
}

export const TICKET_FILTERS = [
  { key: "open", label: "Open" },
  { key: "pending", label: "Pending" },
  { key: "in_progress", label: "In progress" },
  { key: "on_hold", label: "On hold" },
  { key: "forwarded", label: "With GSS" },
  { key: "closed", label: "Closed" },
  { key: "all", label: "All" },
];

/** Prefilled when putting a ticket on hold, and when resuming it. */
export const DEFAULT_HOLD_REASON = "Waiting for the unit to be brought to the MIS office.";
export const DEFAULT_RESUME_NOTE = "Unit received at the MIS office.";

export function unitLabel(value) {
  const unit = UNITS.find((u) => u.value === value);
  return unit ? unit.label : value || "—";
}

/** Quick date ranges for the filters and the report. */
export const PERIODS = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "this_year", label: "This year" },
  { key: "last_year", label: "Last year" },
];

/** A PERIODS key as ["YYYY-MM-DD", "YYYY-MM-DD"], or null. */
export function periodRange(key, now = moment()) {
  const ranges = {
    this_month: () => [now.clone().startOf("month"), now.clone().endOf("month")],
    last_month: () => {
      const m = now.clone().subtract(1, "month");
      return [m.clone().startOf("month"), m.clone().endOf("month")];
    },
    this_quarter: () => [now.clone().startOf("quarter"), now.clone().endOf("quarter")],
    this_year: () => [now.clone().startOf("year"), now.clone().endOf("year")],
    last_year: () => {
      const y = now.clone().subtract(1, "year");
      return [y.clone().startOf("year"), y.clone().endOf("year")];
    },
  };
  if (!ranges[key]) return null;
  return ranges[key]().map((d) => d.format("YYYY-MM-DD"));
}

/** "Jan 1 – Mar 31, 2026" for a filter chip. */
export function rangeLabel(from, to) {
  if (from && to) {
    const a = moment(from);
    const b = moment(to);
    if (a.year() !== b.year()) return `${a.format("MMM D, YYYY")} – ${b.format("MMM D, YYYY")}`;
    return `${a.format("MMM D")} – ${b.format("MMM D, YYYY")}`;
  }
  if (from) return `from ${moment(from).format("MMM D, YYYY")}`;
  if (to) return `until ${moment(to).format("MMM D, YYYY")}`;
  return "";
}

export const REFERRAL_STATUSES = {
  Ongoing: {
    label: "Ongoing",
    color: "#e0901f",
    textColor: "#986215",
    icon: "bi-hourglass-split",
  },
  Closed: {
    label: "Completed",
    color: "#2bb6a3",
    textColor: "#1d7a6d",
    icon: "bi-check-circle-fill",
  },
};

export function referralStatusMeta(status) {
  return REFERRAL_STATUSES[status] || { ...UNKNOWN_STATUS, label: status || "Unknown" };
}

export const REFERRAL_FILTERS = [
  { key: "ongoing", label: "Ongoing" },
  { key: "completed", label: "Completed" },
  { key: "all", label: "All" },
];

export const pillStyle = getStatusPillStyle;

function compactMinutes(minutes, underAMinute) {
  minutes = Math.max(0, minutes);
  if (minutes < 1) return underAMinute;
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 60) return `${days} d`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months} mo`;
  return `${Math.floor(days / 365)} y`;
}

/** Short age such as "3 h" or "12 d", for list columns. */
export function shortAge(value, now = moment()) {
  if (!value) return "";
  return compactMinutes(now.diff(moment(value), "minutes"), "just now");
}

/** Short time between two moments, e.g. how long a ticket took to close. */
export function shortDuration(from, to) {
  if (!from || !to) return "";
  return compactMinutes(moment(to).diff(moment(from), "minutes"), "< 1 min");
}

/** Whole days since `value` (for "waiting long" highlights). */
export function daysSince(value, now = moment()) {
  if (!value) return 0;
  return Math.max(0, now.diff(moment(value), "days"));
}

export function formatDateTime(value) {
  return value ? moment(value).format("MMM D, YYYY h:mm A") : "";
}

export function formatDate(value) {
  return value ? moment(value).format("MMM D, YYYY") : "";
}

/** Pull the best message out of an axios error. */
export function errorMessage(err, fallback = "Something went wrong. Please try again.") {
  const data = err && err.response && err.response.data;
  if (data && data.errors) {
    const first = Object.values(data.errors)[0];
    if (Array.isArray(first) && first.length) return first[0];
  }
  if (data && data.message) return data.message;
  if (err && err.response && err.response.status === 403) {
    return "You don't have access to do that.";
  }
  if (err && !err.response) return "Can't reach the server. Check your connection and try again.";
  return fallback;
}
